/**
 * Slice 1C v1.7 — Alpine store dla ETA koszyka.
 *
 * 2026-06-11 refactor pre-fire (analogicznie do stockEtaHandler):
 *  - Per-row pre-fire: cart.php wstrzykuje data-szczecin-qty/data-supplier-qty na wierszu.
 *  - Lokalna kalkulacja: gdy klient zmienia qty w wierszu i przekracza próg Szczecin
 *    → inline contextual notification w summary cart (NIE floating toast).
 *  - Reguła Huberta 2026-06-11: zero popupu na initial / usuwanie itemu / coupon.
 *    Popup TYLKO przy świadomej zmianie qty wiersza ponad próg.
 *  - REST `/cart-routing` nadal działa (canonical summary), ale ETA reaguje lokalnie
 *    przed odpowiedzią REST (smoother UX).
 *
 * Fetchuje `/wp-json/kablowo/v1/cart-routing` przy init + po każdej zmianie koszyka
 * (event `cart:updated` emitowany z jQuery bridge na `updated_cart_totals`,
 * `removed_from_cart`, `applied_coupon`, `removed_coupon` — patrz main.js).
 */

const REST_BASE = '/wp-json/kablowo/v1';
const INLINE_NOTICE_DURATION_MS = 5_000;
const INLINE_NOTICE_THROTTLE_MS = 5_000;

export const cartEtaStore = {
    loading: true,
    ui: {
        // v1.8-F1: null oznacza "pending fetch" — SSR fallback widoczny.
        message: null,
        class: '',
        hasShortage: false,
        lanbergCount: 0,
    },
    shortageItems: [],
    debounceTimer: null,

    // 2026-06-11 v3: throttle dla floating toast (prawy dolny róg, jak "Produkt dodany do koszyka").
    lastInlineNoticeAt: 0,

    // Per-row state — czytany z DOM przy każdym `cart:updated` (możliwe nowe wiersze).
    rowState: new Map(),

    init() {
        // Initial fetch + initial row snapshot.
        this.captureRowState();
        setTimeout(() => this.fetchRouting(), 100);

        window.addEventListener('cart:updated', () => {
            this.scheduleFetch(500);
        });

        // Watch input.qty na każdym wierszu — pre-fire decyzja czy pokazać inline notice.
        document.addEventListener('change', (e) => this.handleQtyChange(e), true);
        document.addEventListener('input', (e) => this.handleQtyChange(e), true);
    },

    /**
     * Zbiera per-row pre-fire dane z DOM (data-attrib na .bg-white rounded-xl per wiersz koszyka).
     */
    captureRowState() {
        this.rowState.clear();
        document.querySelectorAll('[data-cart-item-key][data-szczecin-qty]').forEach(el => {
            const key = el.dataset.cartItemKey;
            const qtyInput = el.querySelector('input.qty, input[type="number"]');
            this.rowState.set(key, {
                szczeciQty:  parseInt(el.dataset.szczecinQty, 10) || 0,
                supplierQty: parseInt(el.dataset.supplierQty, 10) || 0,
                unit:        el.dataset.rowUnit || 'szt',
                lastQty:     qtyInput ? (parseFloat(qtyInput.value) || 0) : 0,
            });
        });
    },

    /**
     * Wykrywa zmianę qty na wierszu koszyka — jeśli przekroczyła próg Szczecin → inline notice.
     */
    handleQtyChange(e) {
        const target = e.target;
        if (!target || !target.matches || !target.matches('input.qty, input[type="number"]')) return;
        const row = target.closest('[data-cart-item-key]');
        if (!row) return;
        const key = row.dataset.cartItemKey;
        const state = this.rowState.get(key);
        if (!state) return;
        if (!(state.szczeciQty > 0 && state.supplierQty > 0)) return; // brak mixu - nic nie pokazujemy

        const newQty = parseFloat(target.value) || 0;
        const wasUnder = state.lastQty <= state.szczeciQty;
        const isOver = newQty > state.szczeciQty && newQty <= (state.szczeciQty + state.supplierQty);
        if (wasUnder && isOver) {
            this.showInlineNotice(state);
        }
        state.lastQty = newQty;
    },

    /**
     * 2026-06-11 v3: floating toast prawym dolnym rogu (jak "Produkt dodany do koszyka").
     */
    showInlineNotice(state) {
        const now = Date.now();
        if (now - this.lastInlineNoticeAt < INLINE_NOTICE_THROTTLE_MS) return;
        this.lastInlineNoticeAt = now;
        const { szczeciQty, unit } = state;
        window.dispatchEvent(new CustomEvent('show-notification', {
            detail: {
                message: `Powyżej ${szczeciQty} ${unit} realizacja wydłuża się: wysyłamy część od Lanberga.`,
                type: 'warning',
                duration: INLINE_NOTICE_DURATION_MS,
            }
        }));
    },

    scheduleFetch(delay) {
        clearTimeout(this.debounceTimer);
        this.debounceTimer = setTimeout(() => {
            this.captureRowState();
            this.fetchRouting();
        }, delay);
    },

    async fetchRouting() {
        this.loading = true;
        try {
            const r = await fetch(`${REST_BASE}/cart-routing`, {
                credentials: 'same-origin',
            });
            if (!r.ok) throw new Error(`HTTP ${r.status}`);
            const d = await r.json();
            const newMessage = d.ui_summary || '';

            // v1.9-F5: defensywny override gdy REST zwrócił pusty mimo SSR fallback.
            if (newMessage === '' && this.ui.message === null) {
                console.warn('cart-eta: REST zwrócił pusty ui_summary mimo SSR - zostawiam SSR fallback');
                this.shortageItems = Array.isArray(d.shortage_items) ? d.shortage_items : [];
                return;
            }

            this.ui = {
                message: newMessage,
                class: d.ui_class || '',
                hasShortage: !!d.has_shortage,
                lanbergCount: d.lanberg_count || 0,
            };
            this.shortageItems = Array.isArray(d.shortage_items) ? d.shortage_items : [];
        } catch (e) {
            console.error('cartEta fetch failed', e);
        } finally {
            this.loading = false;
        }
    },

    shortageFor(cartItemKey) {
        return this.shortageItems.find(it => it.key === cartItemKey) || null;
    },

    formatQty(n) {
        const num = parseFloat(n);
        if (isNaN(num)) return n;
        return new Intl.NumberFormat('pl-PL', {
            maximumFractionDigits: 2,
        }).format(num);
    }
};
